import { isFulfillable } from './promo-fulfillment-utils';
import { pipe } from './promo-utils';

const isValidBogoData = (promotion) => {
  return promotion.subExperienceTag === 'buyOneGetOne'
    && promotion.anchorItemList.length > 0;
};

// TODO: should we only return true if all properties are the same in all reward tiers?
/**
 * Given an array of reward tiers, this function will return true
 * if all reward tiers contain *at least* the same properties in the first reward tier.
 * @param {Array} rewardTiers
 * @returns {boolean} hasSameTiers
 */
const allTiersHaveSameDetails = (rewardTiers) => {
  const {
    minPurchaseQuantity,
    minPurchaseAmount,
    rewardPercent,
    rewardAmountPerOrder,
    rewardAmountPerItem,
  } = rewardTiers[0];

  return rewardTiers.every((tier) => {
    if (rewardPercent && !tier.rewardPercent) return false;
    if (rewardAmountPerItem && !tier.rewardAmountPerItem) return false;
    if (rewardAmountPerOrder && !tier.rewardAmountPerOrder) return false;
    if (minPurchaseAmount && !tier.minPurchaseAmount) return false;
    if (minPurchaseQuantity && !tier.minPurchaseQuantity) return false;
    return true;
  });
};

const isValidTierData = (pricing) => {
  const rewardTiers = pricing.promotion.reward?.tiers;
  // make sure the item is not part of single-sku bulk
  return !pricing.alternate?.bulk?.value
    && (Array.isArray(rewardTiers) && rewardTiers[0] && typeof rewardTiers[0] === 'object'
      && (rewardTiers[0].minPurchaseAmount || rewardTiers[0].minPurchaseQuantity)
      && (rewardTiers[0].rewardPercent
        || rewardTiers[0].rewardAmountPerOrder
        || rewardTiers[0].rewardAmountPerItem)
        && allTiersHaveSameDetails(rewardTiers));
};

const isValidMSBData = (pricing) => {
  const rewardTiers = pricing.promotion?.reward?.tiers;
  if (rewardTiers?.length === 1) {
    return isValidTierData(pricing);
  }
  return false;
};

const isValidBMSMData = (pricing) => {
  const rewardTiers = pricing.promotion?.reward?.tiers;
  if (rewardTiers?.length >= 2 && rewardTiers.length <= 4) {
    return isValidTierData(pricing);
  }
  return false;
};

// TODO: branch coverage hidden in tests. these are uncovered branches
const isValidPromotionData = (pricing) => {
  const promotion = pricing.promotion;
  if (!promotion
    || typeof promotion.experienceTag !== 'string'
    || !promotion.itemList?.[0]) {
    return false;
  }

  switch (promotion.experienceTag) {
    case 'BOGO':
      return isValidBogoData(promotion);
    case 'MSBONLINE':
      return isValidMSBData(pricing);
    case 'BMSM':
      return isValidBMSMData(pricing);
    default:
      return false;
  }
};

const groupItemsByBrandAndSortAlphabetically = ((products) => (
  products.sort(({ identifiers: item1 }, { identifiers: item2 }) => {
    const brand1 = item1?.brandName;
    const brand2 = item2?.brandName;
    const label1 = item1?.productLabel;
    const label2 = item2?.productLabel;

    if (!brand1 || !label1) return 1;
    if (!brand2 || !label2) return -1;

    if (brand1 < brand2) return -1;
    if (brand1 > brand2) return 1;

    if (label1 < label2) return -1;
    if (label1 > label2) return 1;

    return 0;
  })
));

const filterItems = (items) => items.filter((item) => (
  item?.identifiers
  && item.pricing
  && isFulfillable(item)
  && typeof item.itemId === 'string'
  && item.itemId.length === 9
  && !Number.isNaN(item.itemId)
));

const sortAnchorItemToTopOfList = ((items, anchorId) => items.sort((item1, item2) => {
  if (item1.itemId === anchorId) return -1;
  if (item2.itemId === anchorId) return 1;
  return 0;
}));

const collectItemsMap = (items) => {
  const itemsMap = new Map();
  const itemIds = [];
  items.forEach((item) => {
    itemIds.push(item.itemId);
    itemsMap.set(item.itemId, item);
  });

  return {
    itemIds,
    itemsMap,
  };
};

const getSanitizedRewardTiers = (promotion) => {
  const tiers = promotion?.reward?.tiers;
  if (!tiers) {
    return [{
      tier: 0,
      minPurchaseAmount: 0,
      minPurchaseQuantity: 0,
      rewardPercent: 0,
      rewardAmountPerItem: 0,
      rewardAmountPerOrder: 0,
    }];
  }

  return tiers
    .slice()
    // Sorts promotion tiers from smallest to largest quantity or amount needed to qualify
    .sort((
      { minPurchaseQuantity: aQty, minPurchaseAmount: aAmt },
      { minPurchaseQuantity: bQty, minPurchaseAmount: bAmt }) => {
      return (aQty < bQty || aAmt < bAmt) ? -1 : 1;
    })
    // Guarantees each tier has default value for each key and sets the tier index
    .map((tier, i) => {
      return {
        tier: i,
        minPurchaseAmount: tier.minPurchaseAmount || 0,
        minPurchaseQuantity: tier.minPurchaseQuantity || 0,
        rewardPercent: tier.rewardPercent || 0,
        rewardAmountPerItem: tier.rewardAmountPerItem || 0,
        rewardAmountPerOrder: tier.rewardAmountPerOrder || 0,
      };
    });
};

const getSanitizedItems = (items = [], anchorItem) => {
  return pipe(
    filterItems,
    groupItemsByBrandAndSortAlphabetically,
    (groupedItems) => sortAnchorItemToTopOfList(groupedItems, anchorItem.itemId),
    collectItemsMap
  )(items);
};

/**
 * if BOGO, anchor is NOT in itemList. check anchor fulfillable and at least one free item
 * if MSB, anchor IS in itemList. Check itemList has at least one non-anchor item
 * @param {Array} itemList
 * @returns {boolean} if itemList has minimum number of products
 */
const hasMinimumNumberOfProducts = ({
  isBogo, isMsb, isBmsm, itemIds, isAnchorFulfillable
}) => {
  return (isBogo && isAnchorFulfillable && itemIds.length > 1)
  || ((isMsb || isBmsm) && isAnchorFulfillable && itemIds.length > 1)
  || ((isMsb || isBmsm) && !isAnchorFulfillable && itemIds.length > 0);
};

/**
 * Given a supported promotion, this function will return the sub-experience associated with it.
 * Will return an empty string if no sub-experience can be found.
 * @param {Object} promoData
 * @returns {string} sub-experience
 */
const getSubExperience = (promoData) => {
  if (promoData.experienceTag === 'BOGO') return promoData.subExperienceTag;

  const {
    minPurchaseQuantity, minPurchaseAmount, rewardPercent, rewardAmountPerItem, rewardAmountPerOrder
  } = promoData.reward.tiers[0];

  // We skip 3 and 6 so combo of min quantity/amount and reward matches MSB
  if (promoData.experienceTag === 'BMSM') {
    // BuyXorMoreGetYPercent
    if (minPurchaseQuantity && rewardPercent) return 'BMSM1';
    // BuyXorMoreSaveYDollar
    if (minPurchaseQuantity && rewardAmountPerOrder) return 'BMSM2';
    // BuyXDollarGetYPercent
    if (minPurchaseAmount && rewardPercent) return 'BMSM4';
    // BuyXDollarGetYDollar
    if (minPurchaseAmount && rewardAmountPerOrder) return 'BMSM5';
  }

  // MSBONLINE
  if (promoData.experienceTag === 'MSBONLINE') {
    // BuyXorMoreGetYPercent
    if (minPurchaseQuantity && rewardPercent) return 'MSB1';
    // BuyXorMoreSaveYDollar
    if (minPurchaseQuantity && rewardAmountPerOrder) return 'MSB2';
    // BuyXorMoreSaveYDollarEach
    if (minPurchaseQuantity && rewardAmountPerItem) return 'MSB3';
    // BuyXDollarGetYPercent
    if (minPurchaseAmount && rewardPercent) return 'MSB4';
    // BuyXDollarGetYDollar
    if (minPurchaseAmount && rewardAmountPerOrder) return 'MSB5';
    // BuyXDollarGetYDollarEach
    if (minPurchaseAmount && rewardAmountPerItem) return 'MSB6';
  }

  // default case
  return '';
};

const getSingleItemToDisplay = ({
  isBogo, isMsb, isAnchorFulfillable, itemCount, itemIds
}) => {

  const singleItemIdToDisplay = (isBogo && isAnchorFulfillable && itemCount === 1)
  || (isMsb && itemCount === 1 && !isAnchorFulfillable) ? itemIds[0] : null;

  return singleItemIdToDisplay;
};

const getAnalyticsComponentName = ({ isBogo, isMsb, isBmsm }) => {
  return (isBogo && 'bogo')
  || (isMsb && 'multi-sku bulk')
  || (isBmsm && 'buy more save more') || '';
};

const getAnalyticsSharedSection = ({ isBogo, isMsb, isBmsm }) => {
  return (isBogo && 'bogo')
  || (isMsb && 'multi-sku') // why is there not consistency with analytics component "multi-sku bulk"?
  || (isBmsm && 'buy more save more') || '';
};

const transformItemDataToPromoModel = (products, anchorItem) => {
  // we are omitting itemIds that are BOPIS/STH/etc unfulfillable but are in cart for BODFS... edge case
  const promotion = anchorItem.pricing.promotion;
  const subExperience = getSubExperience(promotion);
  const isBogo = promotion.experienceTag === 'BOGO' && !!subExperience;
  const isMsb = promotion.experienceTag === 'MSBONLINE' && !!subExperience;
  const isBmsm = promotion.experienceTag === 'BMSM' && !!subExperience;

  const analyticsComponent = getAnalyticsComponentName({ isBogo, isMsb, isBmsm });
  const analyticsSharedSection = getAnalyticsSharedSection({ isBogo, isMsb, isBmsm });

  const isAnchorFulfillable = isFulfillable(anchorItem);
  let { itemIds, itemsMap } = getSanitizedItems(products, anchorItem);
  const itemCount = itemIds.length;
  const singleItemIdToDisplay = getSingleItemToDisplay({
    isBogo, isMsb, isAnchorFulfillable, itemCount, itemIds
  });
  if (isBogo) itemIds = [anchorItem.itemId, ...itemIds];

  return {
    products: itemsMap,
    anchorItemImage: anchorItem.media?.image?.url || '',
    isBogo,
    isMsb,
    isBmsm,
    analyticsComponent,
    analyticsSharedSection,
    promoId: promotion.promotionId,
    allItemIds: itemIds,
    hasProducts: hasMinimumNumberOfProducts({
      isBogo, isMsb, isBmsm, itemIds, anchorItem, isAnchorFulfillable
    }),
    itemCount,
    singleItemIdToDisplay,
    isAnchorFulfillable,
    experienceTag: promotion.experienceTag,
    subExperience,
    rewardTiers: getSanitizedRewardTiers(promotion),
  };
};

export {
  pipe,
  isValidPromotionData,
  transformItemDataToPromoModel
};
